import { Check, Globe, Loader2 } from "lucide-react";
import { KB_LANGS, KB_ORIGINAL, flagUrl, langMeta, useKbLang } from "@/lib/kbLang";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

/**
 * Flag language picker for the Knowledge Base. English is the source of truth;
 * choosing a flag AI-translates the current view. The choice is remembered
 * (localStorage) and shared between the landing page and every article.
 */
export function KBLanguageSwitcher({ busy = false, size = "sm" }) {
  const [lang, setLang] = useKbLang();
  const active = langMeta(lang);
  const compact = size === "sm";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="kb-lang-switcher"
          title="Read this in another language"
          className={`flex items-center gap-1.5 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 font-medium text-slate-600 dark:text-slate-300 transition-colors hover:border-primary/40 hover:text-primary data-[state=open]:border-primary/40 data-[state=open]:text-primary ${compact ? "px-2.5 py-1.5 text-xs" : "px-3 py-2 text-sm"}`}>
          {busy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : active ? (
            <img src={flagUrl(active.cc)} alt="" width={20} height={15}
              className="h-[15px] w-5 rounded-[2px] object-cover shadow-sm" />
          ) : (
            <Globe className="h-3.5 w-3.5" />
          )}
          <span>{active ? active.short : "Language"}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52" data-testid="kb-lang-menu">
        <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-slate-400">
          Read this in
        </DropdownMenuLabel>
        <DropdownMenuItem data-testid="kb-lang-original"
          onClick={() => setLang(KB_ORIGINAL)}
          className="flex items-center gap-2">
          <Globe className="h-4 w-4 text-slate-500" />
          <span className="flex-1">English (original)</span>
          {lang === KB_ORIGINAL && <Check className="h-3.5 w-3.5 text-primary" />}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {KB_LANGS.map((l) => (
          <DropdownMenuItem key={l.code} data-testid={`kb-lang-${l.code}`}
            onClick={() => setLang(l.code)}
            className="flex items-center gap-2">
            <img src={flagUrl(l.cc)} alt="" width={20} height={15}
              className="h-[15px] w-5 rounded-[2px] object-cover shadow-sm" />
            <span className="flex-1">{l.label}</span>
            {lang === l.code && <Check className="h-3.5 w-3.5 text-primary" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
